import jquery from 'jquery';
import {capitalizeFirstLetter} from 'js-libs/sweetcommons/strings';

import {gt} from 'app/utils/i18n';

import type {ReactNode} from 'react';

type StringOrElementType = ReactNode | string | number;

export {capitalizeFirstLetter};
// https://facebook.github.io/react/docs/dom-elements.html#dangerouslysetinnerhtml
export const markAsSafe = (
  string: string
): {
  __html: string;
} => ({
  __html: string,
});
const entityMap = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
  '/': '&#x2F;',
  '`': '&#x60;',
  '=': '&#x3D;',
};
export function escapeHtml(str: string) {
  return String(str).replace(/[&<>"'`=/]/g, (s) => entityMap[s]);
}
const IMG_SIZES = {
  large: '1000x1000/',
  small: '500x500/',
  original: '',
};
// image etensions which we can be resized
const SAFE_IMAGE_TYPES = ['png', 'jpe?g'];
const SAFE_IMAGES_REGEX = new RegExp(`.(${SAFE_IMAGE_TYPES.join('|')})$`, 'i');

export const cdnImageSrc = (
  string: string | undefined,
  mediaURL: string,
  size: string
) =>
  /* this should capture both formats, old and new:
    - old: 20s07/46842/3256c6d8/user-provided-name.png
    - new: files/46842/20s07/75ac8278-22e4-4067-bfbf-6bc117806530.png
  */
  string !== undefined
    ? string
        .replace(
          /src="(%2F|\/)?((files(%2F|\/)\d+(%2F|\/))?(\d\ds\d\d))/g,
          `src="${mediaURL}${IMG_SIZES[size]}$2`
        )
        .replace(
          /src-original="(%2F|\/)?(\d\ds\d\d)/g,
          `src="${mediaURL}${IMG_SIZES[size]}$2`
        )
    : string;

export const cdnImageRef = (
  image_ref: string,
  mediaURL: string,
  size: string
) => `${mediaURL}${IMG_SIZES[size]}${image_ref.replace(/^(\/|%2F)/, '')}`;

export const safeCdnImageRef = (
  image_ref: string,
  mediaURL: string,
  size: string
) => {
  if (!image_ref.match(SAFE_IMAGES_REGEX)) {
    size = 'original';
  }

  return cdnImageRef(image_ref, mediaURL, size);
};

export const removeDataImages = (content: string): string => {
  // don't parse if we don't need to
  if (content.indexOf('"data:') !== -1) {
    let $content = jquery('<div />', {
      html: content,
    });
    jquery('img[src^="data:"]', $content).remove();
    return $content.html();
  }

  return content;
};

export function toOxfordCommaList(
  items: StringOrElementType[],
  lastSep?: string
): string {
  lastSep = lastSep ?? gt('and');

  if (items.length == 2) {
    return items.join(` ${lastSep} `);
  } else if (items.length > 2) {
    return items
      .slice(0, items.length - 1)
      .concat(`${lastSep} ${items.slice(-1).toString()}`)
      .join(', ');
  }

  return items.join(', ');
}

export function numberToLetter(num: number): string {
  // 'A' char code is 65, so adding the number to 65 gives us the char code for the letter
  // take modulus of 26 to wrap back to A after Z
  return String.fromCharCode(65 + (num % 26));
}

export async function decrypt(encryptedBase64, password) {
  // Decode the Base64 string to get the raw data
  const encryptedBytes = Uint8Array.from(atob(encryptedBase64), (c) =>
    c.charCodeAt(0)
  );
  const iv = encryptedBytes.slice(0, 16); // IV is typically the first 16 bytes
  const encryptedData = encryptedBytes.slice(16); // The rest is the ciphertext

  // convert the password to crypto ready format.
  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(password),
    {name: 'PBKDF2'},
    false, // The derived key is not extractable
    ['deriveBits', 'deriveKey'] // The key can be used only for decryption
  );

  // Derive the key from the password
  const key = await crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: encoder.encode(password),
      iterations: 100,
      hash: 'SHA-256',
    },
    keyMaterial,
    {name: 'AES-CBC', length: 256},
    true,
    ['encrypt', 'decrypt']
  );

  // Decrypt the data
  const decrypted = await crypto.subtle.decrypt(
    {
      name: 'AES-CBC',
      iv,
    },
    key,
    encryptedData
  );

  // Convert the decrypted data back to a UTF-8 string
  const decoder = new TextDecoder();
  return decoder.decode(decrypted);
}

const stripHTML = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.innerText || '';
};

export const countWords = (htmlString: string): number => {
  let text = stripHTML(htmlString);

  // Normalize spaces and remove unwanted characters
  text = text.replace(/(^\s*)|(\s*$)/gi, ' ');
  text = text.replace(/\u00a0/g, ' ');
  text = text.replace(/[ ]{2,}/gi, ' ');
  text = text.replace(/\n /, '\n');
  text = text.replace(
    /[\u200A\u200C-\u200D\uFEFF\u202F\u2000-\u2009\u205F\u3000\u1680\u00A0\u0020]/g,
    ' '
  );
  text = text.replace(/[\u200B']/g, '');
  text = text.replace(/\t+/g, ' ');
  text = text.replace(/[^\w\s]|_/g, ' ');

  // Split by spaces and filter out empty strings
  const words = text.split(/\s+/).filter((word) => word.length > 0);

  return words.length;
};

export function maskedString(rawString: string, limit: number) {
  return `${rawString.substring(0, limit)}...${rawString.substring(
    rawString.length - limit
  )}`;
}

export function isEmptyString(input: string): boolean {
  // returns true if the string is empty (&nbsp; <br> etc all counted as empty)

  const div = document.createElement('div');
  div.innerHTML = input;
  const cleaned = div.textContent || div.innerText || '';

  return cleaned.trim().length === 0;
}
